import { useState, useEffect, useMemo, useRef } from "react";
import { useSocket } from "../../../contexts/SocketContext";
import { fetchSuppliersV2 } from "../../models/suppliers/suppliersModel";
import { fetchCostPrices } from "../../models/shared/costPricesModel";
import { fetchTaskDetail } from "../../models/shared/taskDetailModel";
import { fetchApprovalHistory } from "../../models/price-drawer/pricingModel";
import { seedQuotePricing } from "../../models/price-drawer/quoteEditingPolicy";
import { loadConditionLabels, loadSideLabels } from "../../helpers";

export function usePriceUpdateData({ card, open, onPartsLoaded, setApprovalHistory, resetEditing, notify }) {
  const { socket: mainSocket } = useSocket();
  const [parts, setParts] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [costPrices, setCostPrices] = useState([]);
  const [costPricesLoading, setCostPricesLoading] = useState(true);
  const [quotePricing, setQuotePricing] = useState({});
  const [pricingTimeline, setPricingTimeline] = useState([]);
  const [taskCustomer, setTaskCustomer] = useState(null);
  const [taskCustomerType, setTaskCustomerType] = useState(null);
  const [pricingReload, setPricingReload] = useState(0);
  const taskLoadRequestRef = useRef(0);
  const [, setConditionLabelsReady] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    Promise.all([loadConditionLabels(), loadSideLabels()]).then(() => { if (active) setConditionLabelsReady(true); });
    return () => { active = false; };
  }, [open]);

  // Keep parent badge accurate
  useEffect(() => {
    if (!open || !card?.id || !parts.length) return;
    onPartsLoaded?.(card.id, parts);
  }, [parts, card?.id, open]); // eslint-disable-line

  // Fetch parts + approval history on open
  useEffect(() => {
    const requestId = ++taskLoadRequestRef.current;
    if (!open || !card?.id) { setParts([]); setQuotePricing({}); setApprovalHistory([]); setTaskCustomer(null); setTaskCustomerType(null); return undefined; }
    setTaskLoading(true);
    setParts([]);
    setQuotePricing({});
    setApprovalHistory([]);
    setTaskCustomer(null);
    setTaskCustomerType(null);
    resetEditing();
    fetchTaskDetail(card.id)
      .then(taskData => {
        if (requestId !== taskLoadRequestRef.current) return;
        if (!taskData) return;
        const taskObj = taskData.task ?? taskData;
        setPricingTimeline(taskObj.timeline || []);
        if (taskObj.customer) setTaskCustomer(taskObj.customer);
        const ct = taskObj.customer_type ?? taskObj.customer?.type ?? taskObj.customer?.customer_type ?? null;
        if (ct != null) setTaskCustomerType(ct);
        const rawParts = taskData.parts ?? taskObj.parts ?? [];
        setParts(rawParts.map(p => ({
          ...p,
          supplier_quotes: p.supplier_quotes ?? p.quotes ?? [],
        })));
      })
      .catch(error => {
        if (requestId === taskLoadRequestRef.current) {
          notify.error(error?.response?.data?.message || error.message || "Could not load query pricing");
        }
      })
      .finally(() => {
        if (requestId === taskLoadRequestRef.current) setTaskLoading(false);
      });
    fetchApprovalHistory(card.id)
      .then(list => {
        if (requestId === taskLoadRequestRef.current && list) setApprovalHistory(list);
      })
      .catch(() => {});
    return () => {
      if (requestId === taskLoadRequestRef.current) taskLoadRequestRef.current += 1;
    };
  }, [open, card?.id, pricingReload]); // eslint-disable-line

  // Reload the active policy whenever this drawer opens.
  useEffect(() => {
    if (!open) return;
    setCostPricesLoading(true);
    fetchCostPrices({ force: true }).then(data => {
      setCostPrices(data);
      setCostPricesLoading(false);
    });
  }, [open, pricingReload]);

  // On load: restore GP% for already-selected quotes that have a saved selling_price
  // (that value is authoritative — it's what's actually persisted). Every other
  // quote — unselected, or not yet priced — gets a tier-lookup preview so every
  // row shows a sensible number immediately without waiting on "Calculate prices".
  // These previews are NOT persisted anywhere until their quote is selected.
  useEffect(() => {
    if (!parts.length || costPricesLoading) return;
    setQuotePricing(prev => seedQuotePricing(prev, parts, costPrices));
  }, [parts, costPrices, costPricesLoading]); // eslint-disable-line

  // Real-time socket updates from other users
  useEffect(() => {
    if (!mainSocket || !open || !card?.id) return;

    const handleQuotesUpdated = (data) => {
      if (data?.data?.task_id !== card.id) return;
      const { quote, event } = data.data;
      if (!quote) return;
      setParts(prev => prev.map(p => {
        if (p.id !== quote.query_part_id) return p;
        const qs = p.supplier_quotes ?? [];
        if (event === "quote_disabled") {
          return { ...p, supplier_quotes: qs.filter(q => q.id !== quote.id) };
        }
        const exists = qs.some(q => q.id === quote.id);
        return {
          ...p,
          supplier_quotes: exists
            ? qs.map(q => q.id === quote.id ? { ...q, ...quote } : q)
            : [...qs, quote],
        };
      }));
    };

    const handlePartsUpdated = (data) => {
      if (data?.data?.task_id !== card.id) return;
      const { part, event } = data.data;
      if (!part) return;
      if (event === "part_disabled") {
        setParts(prev => prev.filter(p => p.id !== part.id));
      } else {
        setParts(prev => prev.map(p => p.id === part.id ? { ...p, ...part } : p));
      }
    };

    mainSocket.on("query:quotes_updated", handleQuotesUpdated);
    mainSocket.on("query:parts_updated", handlePartsUpdated);
    return () => {
      mainSocket.off("query:quotes_updated", handleQuotesUpdated);
      mainSocket.off("query:parts_updated", handlePartsUpdated);
    };
  }, [mainSocket, open, card?.id]); // eslint-disable-line

  // Supplier list (cached)
  useEffect(() => {
    if (allSuppliers.length) return;
    fetchSuppliersV2().then(s => setAllSuppliers(s));
  }, []); // eslint-disable-line

  const supplierOptions = useMemo(() =>
    allSuppliers.map(s => ({
      value: s.id,
      label: s.name && s.name !== s.number ? s.name : s.number,
      name: s.name || "",
      number: s.number || "",
    })),
    [allSuppliers]
  );


  return { parts, setParts, taskLoading, setTaskLoading, allSuppliers, setAllSuppliers, costPrices, setCostPrices, costPricesLoading, setCostPricesLoading, quotePricing, setQuotePricing, pricingTimeline, setPricingTimeline, taskCustomer, setTaskCustomer, taskCustomerType, setTaskCustomerType, pricingReload, setPricingReload, supplierOptions };
}
